import { useState, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import type { Routine, EditingRoutine, User, Exercise, ExerciseCatalogEntry } from '../types';
import { clampNonNegativeNumber } from '../utils/dataSanitization';
import { OFFLINE_CACHE_KEYS, readCache, type LoadSource, writeCache } from '../utils/offlineCache';

export function useRoutines(
  user: User | null,
  showNotification: (msg: string, type: 'success' | 'error') => void,
  showConfirm: (msg: string, onConfirm: () => void) => void,
  ensureExerciseEntry: (name: string) => Promise<ExerciseCatalogEntry>
) {
  const [routines, setRoutines] = useState<Routine[]>([]);
  const [routinesLoading, setRoutinesLoading] = useState(false);
  const [editingRoutine, setEditingRoutine] = useState<EditingRoutine | null>(null);

  const loadRoutines = useCallback(async (): Promise<LoadSource> => {
    if (!user) return 'empty';
    setRoutinesLoading(true);
    try {
      const { data, error } = await supabase
        .from('routines')
        .select(`
          id,
          user_id,
          name,
          routine_exercises (
            id,
            exercise_id,
            order_index,
            default_sets,
            default_reps,
            exercise_catalog ( id, name )
          )
        `)
        .eq('user_id', user.id);

      if (error) throw error;

      const mappedRoutines: Routine[] = (data || []).map((routine: any) => ({
        id: String(routine.id),
        user_id: routine.user_id,
        name: routine.name,
        exercises: (routine.routine_exercises || [])
          .slice()
          .sort((a: any, b: any) => (a.order_index ?? 0) - (b.order_index ?? 0))
          .map((ex: any) => ({
            exercise_id: ex.exercise_id,
            name: ex.exercise_catalog?.name || 'Ejercicio',
            sets: ex.default_sets ?? 3,
            reps: ex.default_reps ?? 10,
          })),
      }));

      setRoutines(mappedRoutines);
      await writeCache(OFFLINE_CACHE_KEYS.routines(user.id), mappedRoutines, user.id);
      return 'network';
    } catch (error) {
      console.error('Error cargando rutinas:', error);
      const cached = await readCache<Routine[]>(OFFLINE_CACHE_KEYS.routines(user.id), user.id);
      if (cached) {
        setRoutines(cached);
        return 'cache';
      }
      return 'empty';
    } finally {
      setRoutinesLoading(false);
    }
  }, [user]);

  const createRoutine = () => {
    setEditingRoutine({ name: '', exercises: [] });
  };

  const addExercise = () => {
    if (!editingRoutine) return;
    setEditingRoutine({
      ...editingRoutine,
      exercises: [...editingRoutine.exercises, { name: '', sets: 3, reps: 10 }],
    });
  };

  const addExerciseFromCatalog = (entry: { id: string; name: string }, sets = 3, reps = 10) => {
    if (!editingRoutine) return;
    setEditingRoutine({
      ...editingRoutine,
      exercises: [...editingRoutine.exercises, { exercise_id: entry.id, name: entry.name, sets, reps }],
    });
  };

  const updateExercise = (index: number, field: string, value: any) => {
    if (!editingRoutine) return;
    const newExercises = [...editingRoutine.exercises];
    let sanitizedValue = value;
    if (field === 'sets' || field === 'reps') {
      sanitizedValue = clampNonNegativeNumber(value);
    }
    if (field === 'name' && typeof value === 'string') {
      sanitizedValue = value.slice(0, 50);
    }
    (newExercises[index] as any)[field] = sanitizedValue;
    setEditingRoutine({ ...editingRoutine, exercises: newExercises });
  };

  const removeExercise = (index: number) => {
    if (!editingRoutine) return;
    const newExercises = editingRoutine.exercises.filter((_, i) => i !== index);
    setEditingRoutine({ ...editingRoutine, exercises: newExercises });
  };

  const saveRoutine = async (onSuccess: () => Promise<void>) => {
    if (!editingRoutine || !editingRoutine.name || editingRoutine.exercises.length === 0 || !user) return;

    try {
      const routineName = editingRoutine.name.trim();
      if (!routineName) {
        showNotification('El nombre de la rutina es obligatorio', 'error');
        return;
      }

      const hasEmptyExerciseName = editingRoutine.exercises.some(ex => !ex.name || !ex.name.trim());
      
      if (hasEmptyExerciseName) {
        showNotification('Completá el nombre de todos los ejercicios', 'error');
        return;
      }

      let routineId = editingRoutine.id;
      if (routineId) {
        const { error } = await supabase.from('routines').update({ name: routineName }).eq('id', routineId);
        if (error) throw error;
      } else {
        const { data, error } = await supabase.from('routines').insert({ user_id: user.id, name: routineName }).select('id').single();
        if (error) throw error;
        routineId = data.id;
      }

      const resolvedExercises: Exercise[] = [];
      for (const ex of editingRoutine.exercises) {
        const trimmedName = ex.name?.trim();
        if (!trimmedName) continue;
        let exerciseId = ex.exercise_id;
        if (!exerciseId) {
          const catalogEntry = await ensureExerciseEntry(trimmedName);
          exerciseId = catalogEntry.id;
        }
        resolvedExercises.push({ ...ex, exercise_id: exerciseId, name: trimmedName });
      }

      await supabase.from('routine_exercises').delete().eq('routine_id', routineId);
      const payload = resolvedExercises.map((ex, index) => ({
        routine_id: routineId,
        exercise_id: ex.exercise_id,
        order_index: index,
        default_sets: ex.sets,
        default_reps: ex.reps,
      }));

      if (payload.length > 0) {
        const { error } = await supabase.from('routine_exercises').insert(payload);
        if (error) throw error;
      }

      await onSuccess();
      setEditingRoutine(null);
      showNotification('Rutina guardada', 'success');
    } catch (error) {
      console.error('Error guardando rutina:', error);
      showNotification('Error al guardar la rutina', 'error');
    }
  };

  const deleteRoutine = async (id: string, isDemoId: (id: string) => boolean) => {
    if (isDemoId(id)) return;
    const routine = routines.find(r => r.id === id);
    showConfirm(`¿Eliminar la rutina "${routine?.name || 'esta rutina'}"?`, async () => {
      try {
        await supabase.from('routine_exercises').delete().eq('routine_id', id);
        const { error } = await supabase.from('routines').delete().eq('id', id);
        if (error) throw error;
        setRoutines(prev => prev.filter(r => r.id !== id));
        showNotification('Rutina eliminada', 'success');
      } catch (error) {
        console.error('Error eliminando rutina:', error);
        showNotification('Error al eliminar la rutina', 'error');
      }
    });
  };

  return {
    routines,
    setRoutines,
    routinesLoading,
    editingRoutine,
    setEditingRoutine,
    loadRoutines,
    createRoutine,
    addExercise,
    addExerciseFromCatalog,
    updateExercise,
    removeExercise,
    saveRoutine,
    deleteRoutine,
  };
}
